import React, { useEffect, useRef } from "react";
import { useWeather } from "./WeatherContext";
import { gustyWind } from "./bostonWeather";

// The weather drawn behind the home page hero. It is one canvas that ignores the
// pointer and pauses while scrolled out of view. Visitors whose system asks for
// reduced motion see one still frame of the live weather (sky only: stars, sun or
// moon, clouds or fog, never falling rain or snow). It only moves for them when they
// pick a weather, and lightning then does not flash the whole hero.
//
// Each kind of weather is a list of layers. A layer is a function called every
// frame with (ctx, dt, t, wind, dark): seconds since the last frame, seconds
// overall, the gusting wind in mph (+ is to the right) and whether the dark theme is on.

const rand = (min, max) => min + Math.random() * (max - min);

// Keeps x on screen by carrying it round to the other side.
const wrap = (x, W, margin) => (x > W + margin ? x - W - 2 * margin : x < -margin ? x + W + 2 * margin : x);

function rain(W, H, { count, drizzle }) {
  const drops = Array.from({ length: count }, () => ({
    x: rand(0, W),
    y: rand(-H, H),
    far: Math.random() < 0.5,
    speed: drizzle ? rand(350, 500) : rand(900, 1300),
    len: drizzle ? rand(5, 9) : rand(14, 24),
  }));
  const pushPerMph = drizzle ? 18 : 25;

  return (ctx, dt, t, wind, dark) => {
    const vx = wind * pushPerMph;
    for (const d of drops) {
      const depth = d.far ? 0.7 : 1;
      d.y += d.speed * depth * dt;
      d.x = wrap(d.x + vx * depth * dt, W, 40);
      if (d.y - d.len > H) {
        d.y = rand(-60, -10);
        d.x = rand(0, W);
      }
    }
    // Far drops are fainter and thinner, which gives the rain some depth.
    for (const far of [true, false]) {
      ctx.beginPath();
      for (const d of drops) {
        if (d.far !== far) continue;
        const speed = d.speed * (far ? 0.7 : 1);
        const k = d.len / Math.hypot(vx, speed);
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - vx * k, d.y - speed * k);
      }
      const alpha = (far ? 0.6 : 1) * (drizzle ? 0.7 : 1);
      ctx.strokeStyle = dark ? `rgba(205, 218, 240, ${0.45 * alpha})` : `rgba(60, 90, 135, ${0.38 * alpha})`;
      ctx.lineWidth = far ? 1 : 1.4;
      ctx.lineCap = "round";
      ctx.stroke();
    }
  };
}

function snow(W, H, { count, blizzard }) {
  const flakes = Array.from({ length: count }, () => {
    const r = blizzard ? rand(1, 2.4) : rand(1.2, 3.6);
    return {
      x: rand(0, W),
      y: rand(-H, H),
      r,
      speed: blizzard ? rand(140, 260) : 22 + r * rand(10, 18),
      phase: rand(0, Math.PI * 2),
      sway: rand(8, 20),
      // the biggest flakes are drawn as six-armed crystals
      crystal: !blizzard && r > 2.8,
      spin: rand(-0.6, 0.6),
    };
  });
  const pushPerMph = blizzard ? 7 : 6;
  const sprites = {};

  return (ctx, dt, t, wind, dark) => {
    for (const f of flakes) {
      f.y += f.speed * dt;
      f.x = wrap(f.x + (wind * pushPerMph + Math.sin(t * 1.1 + f.phase) * f.sway) * dt, W, 10);
      if (f.y - f.r > H) {
        f.y = rand(-30, -5);
        f.x = rand(0, W);
      }
    }
    const sprite = sprites[dark] || (sprites[dark] = flakeSprite(dark));
    ctx.beginPath();
    for (const f of flakes) {
      if (!f.crystal) {
        ctx.drawImage(sprite, f.x - f.r * 2, f.y - f.r * 2, f.r * 4, f.r * 4);
        continue;
      }
      const R = f.r * 2;
      for (let arm = 0; arm < 3; arm++) {
        const a = t * f.spin + f.phase + (arm * Math.PI) / 3;
        const dx = Math.cos(a) * R;
        const dy = Math.sin(a) * R;
        ctx.moveTo(f.x - dx, f.y - dy);
        ctx.lineTo(f.x + dx, f.y + dy);
      }
    }
    ctx.strokeStyle = dark ? "rgba(255, 255, 255, 0.85)" : "rgba(125, 150, 190, 0.75)";
    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.stroke();
  };
}

// A soft round flake, drawn once per theme and stamped for every flake. White
// vanishes on the light page, so there the flakes are a pale icy blue.
function flakeSprite(dark) {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d");
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  if (dark) {
    g.addColorStop(0, "rgba(255, 255, 255, 0.95)");
    g.addColorStop(0.45, "rgba(255, 255, 255, 0.75)");
    g.addColorStop(1, "rgba(255, 255, 255, 0)");
  } else {
    g.addColorStop(0, "rgba(150, 172, 210, 0.9)");
    g.addColorStop(0.45, "rgba(150, 172, 210, 0.65)");
    g.addColorStop(1, "rgba(150, 172, 210, 0)");
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  return canvas;
}

// A four-pointed twinkle with curved sides.
function sparkle(ctx, x, y, R) {
  ctx.moveTo(x, y - R);
  ctx.quadraticCurveTo(x, y, x + R, y);
  ctx.quadraticCurveTo(x, y, x, y + R);
  ctx.quadraticCurveTo(x, y, x - R, y);
  ctx.quadraticCurveTo(x, y, x, y - R);
}

function stars(W, H, { count }) {
  const list = Array.from({ length: count }, () => ({
    x: rand(0, W),
    y: rand(0, H * 0.8),
    r: rand(0.6, 1.5),
    bright: Math.random() < 0.3,
    alpha: rand(0.4, 1),
    phase: rand(0, Math.PI * 2),
    twinkle: rand(0.8, 2.2),
  }));
  // A shooting star now and then; the first comes soon enough to be noticed.
  let nextShot = null;
  let shot = null;

  return (ctx, dt, t, wind, dark) => {
    // On the light page plain dots look like dust, so stars there are only
    // twinkles: more of them, and kept from fading too far between twinkles.
    for (const s of list) {
      const twinkles = s.bright || (!dark && s.r > 1.2);
      if (!dark && !twinkles) continue;
      const wave = Math.sin(t * s.twinkle + s.phase);
      ctx.fillStyle = dark
        ? `rgba(255, 250, 230, ${s.alpha * (0.6 + 0.4 * wave)})`
        : `rgba(85, 95, 190, ${(0.5 + 0.5 * s.alpha) * (0.7 + 0.3 * wave) * 0.85})`;
      ctx.beginPath();
      if (twinkles) sparkle(ctx, s.x, s.y, (s.r + 1) * (dark ? 2.6 : 3.4));
      else ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    if (nextShot === null) nextShot = t + rand(4, 9);
    if (!shot && t >= nextShot) {
      shot = { start: t, x: rand(W * 0.3, W * 0.95), y: rand(0, H * 0.3) };
      nextShot = t + rand(14, 30);
    }
    if (shot) {
      const age = (t - shot.start) / 0.9;
      if (age >= 1) {
        shot = null;
      } else {
        const travel = Math.min(W * 0.35, 320) * age;
        const hx = shot.x - travel;
        const hy = shot.y + travel * 0.45;
        const tail = 90 * Math.sin(Math.PI * age);
        const tx = hx + tail;
        const ty = hy - tail * 0.45;
        const color = dark ? "255, 250, 230" : "70, 80, 140";
        const gradient = ctx.createLinearGradient(hx, hy, tx, ty);
        gradient.addColorStop(0, `rgba(${color}, ${dark ? 0.9 : 0.6})`);
        gradient.addColorStop(1, `rgba(${color}, 0)`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.6;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(hx, hy);
        ctx.lineTo(tx, ty);
        ctx.stroke();
      }
    }
  };
}

// The light of a clear sky comes from the top left, so it falls on the photo
// first and fades out across the text. On the light page it is the sun; on the
// dark page it is the moon, also at night when the sky is otherwise just stars.
function skyLight(W, H, { night }) {
  const cx = W * 0.12;
  const cy = H * 0.05;
  const sun = sunlight(cx, cy, Math.max(W, H) * 0.55, W, H);
  const moon = moonlight(cx, cy, Math.max(W, H) * 0.45, W, H);

  return (ctx, dt, t, wind, dark) => {
    if (dark) moon(ctx, t);
    else if (!night) sun(ctx, t);
  };
}

function sunlight(cx, cy, radius, W, H) {
  const rays = 9;

  return (ctx, t) => {
    const breathe = 0.85 + 0.15 * Math.sin(t * 0.5);
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    glow.addColorStop(0, `rgba(255, 170, 60, ${0.24 * breathe})`);
    glow.addColorStop(1, "rgba(255, 190, 90, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Soft rays turning very slowly.
    const rayFill = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.1);
    rayFill.addColorStop(0, "rgba(255, 175, 70, 0.10)");
    rayFill.addColorStop(1, "rgba(255, 190, 90, 0)");
    ctx.fillStyle = rayFill;
    ctx.beginPath();
    for (let i = 0; i < rays; i++) {
      const angle = t * 0.015 + (i * Math.PI * 2) / rays;
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius * 1.1, angle - 0.06, angle + 0.06);
      ctx.closePath();
    }
    ctx.fill();
  };
}

// Moonlight has no rays: a cool silver glow, a brighter core near the moon and
// a faint ring around it, like the halo the moon gets through thin high cloud.
function moonlight(cx, cy, radius, W, H) {
  return (ctx, t) => {
    const breathe = 0.9 + 0.1 * Math.sin(t * 0.3);
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    glow.addColorStop(0, `rgba(200, 215, 255, ${0.13 * breathe})`);
    glow.addColorStop(0.4, `rgba(180, 200, 245, ${0.05 * breathe})`);
    glow.addColorStop(1, "rgba(180, 200, 245, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    const coreRadius = Math.min(radius * 0.3, 180);
    const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius);
    core.addColorStop(0, `rgba(235, 240, 255, ${0.16 * breathe})`);
    core.addColorStop(1, "rgba(235, 240, 255, 0)");
    ctx.fillStyle = core;
    ctx.fillRect(0, 0, W, H);

    const haloRadius = coreRadius * 1.9;
    const halo = ctx.createRadialGradient(cx, cy, haloRadius * 0.82, cx, cy, haloRadius);
    halo.addColorStop(0, "rgba(210, 222, 255, 0)");
    halo.addColorStop(0.55, `rgba(210, 222, 255, ${0.05 * breathe})`);
    halo.addColorStop(1, "rgba(210, 222, 255, 0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, W, H);
  };
}

// Clouds match the ones that pass over the page when switching to dark mode
// (.cloud in Navbar.css): a flat rounded body, 5:2, with two round bumps on top.
// Each part is [left, top, width, height] as fractions of the cloud's width,
// measured from the top of the taller bump.
const BODY_HEIGHT = 0.4;
const CLOUD_HEIGHT = BODY_HEIGHT * 1.52;
const CLOUD_PARTS = [
  [0, BODY_HEIGHT * 0.52, 1, BODY_HEIGHT], // body
  [0.14, BODY_HEIGHT * (0.52 - 0.36), 0.3, 0.3], // small bump
  [0.38, 0, 0.38, 0.38], // big bump
];

// A cloud drawn once as a picture, then moved around. The parts are drawn opaque on
// their own canvas first, so where they overlap the cloud doesn't get darker.
function cloudSprite(width, { color, alpha }) {
  const shape = document.createElement("canvas");
  shape.width = Math.ceil(width);
  shape.height = Math.ceil(width * CLOUD_HEIGHT);
  const sctx = shape.getContext("2d");
  sctx.fillStyle = `rgb(${color})`;
  // Each part is a pill (a circle when it's square): two half circles joined by a rectangle.
  for (const [l, t, w, h] of CLOUD_PARTS) {
    const r = (h * width) / 2;
    const cy = t * width + r;
    const x0 = l * width + r;
    const x1 = (l + w) * width - r;
    sctx.beginPath();
    sctx.arc(x0, cy, r, Math.PI / 2, (Math.PI * 3) / 2);
    sctx.arc(x1, cy, r, -Math.PI / 2, Math.PI / 2);
    sctx.closePath();
    sctx.fill();
  }
  const canvas = document.createElement("canvas");
  canvas.width = shape.width;
  canvas.height = shape.height;
  const ctx = canvas.getContext("2d");
  ctx.globalAlpha = alpha;
  ctx.drawImage(shape, 0, 0);
  return canvas;
}

function clouds(W, H, { count, heavy }) {
  // Same sizing as the dark-mode clouds: a share of the screen width, so phones get
  // smaller clouds instead of desktop-sized ones covering the hero.
  const base = Math.min(Math.max(0.24 * W, 110), 380);
  const list = Array.from({ length: count }, () => {
    const width = base * rand(0.75, 1.15);
    return {
      width,
      x: rand(-width, W),
      y: rand(-0.05 * H, 0.4 * H),
      depth: rand(0.5, 1),
    };
  });
  // Sprites depend on the theme, so they are made when first needed in each one.
  // The grey is the dark-mode clouds' #dbdbdb, see-through enough that the hero text
  // stays easy to read on top of it.
  const spriteFor = (c, dark) => {
    const key = dark ? "darkSprite" : "lightSprite";
    if (!c[key]) {
      c[key] = cloudSprite(
        c.width,
        dark
          ? { color: "219, 219, 219", alpha: heavy ? 0.22 : 0.16 }
          : { color: heavy ? "205, 208, 214" : "219, 219, 219", alpha: heavy ? 0.85 : 0.7 }
      );
    }
    return c[key];
  };

  return (ctx, dt, t, wind, dark) => {
    const direction = wind < 0 ? -1 : 1;
    for (const c of list) {
      const speed = (8 + Math.abs(wind) * 2.5) * c.depth * direction;
      c.x += speed * dt;
      if (c.x > W) c.x = -c.width;
      if (c.x < -c.width) c.x = W;
      ctx.drawImage(spriteFor(c, dark), c.x, c.y);
    }
  };
}

function fog(W, H, { intensity }) {
  const bands = Array.from({ length: 4 }, (_, i) => ({
    x: rand(-W, 0),
    y: H * (0.1 + i * 0.25),
    speed: rand(4, 12) * (i % 2 ? 1 : -1),
  }));
  const bandWidth = W * 1.6;
  const bandHeight = H * 0.5;

  return (ctx, dt, t, wind, dark) => {
    ctx.fillStyle = dark ? `rgba(200, 205, 215, ${0.06 * intensity})` : `rgba(170, 175, 185, ${0.12 * intensity})`;
    ctx.fillRect(0, 0, W, H);
    for (const b of bands) {
      b.x += (b.speed + wind * 1.5) * dt;
      if (b.x > W) b.x = -bandWidth;
      if (b.x < -bandWidth) b.x = W;
      const cx = b.x + bandWidth / 2;
      ctx.save();
      ctx.translate(cx, b.y);
      ctx.scale(bandWidth / bandHeight, 1);
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, bandHeight / 2);
      const a = (dark ? 0.16 : 0.26) * intensity;
      g.addColorStop(0, dark ? `rgba(215, 220, 230, ${a})` : `rgba(150, 160, 175, ${a})`);
      g.addColorStop(1, "rgba(150, 160, 175, 0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, bandHeight / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };
}

// A faint wash over the whole hero, used to darken it for storms.
function tint(W, H, { light, dark: darkColor }) {
  return (ctx, dt, t, wind, dark) => {
    ctx.fillStyle = dark ? darkColor : light;
    ctx.fillRect(0, 0, W, H);
  };
}

function lightning(W, H, { flash }) {
  let nextStrike = null;
  let strike = null;

  const makeBolt = () => {
    const points = [];
    let x = rand(W * 0.15, W * 0.85);
    let y = 0;
    const bottom = rand(H * 0.5, H * 0.85);
    while (y < bottom) {
      points.push([x, y]);
      y += rand(18, 32);
      x += rand(-18, 18);
    }
    points.push([x, y]);
    // One short fork part way down.
    const forkFrom = Math.floor(points.length * rand(0.3, 0.6));
    const fork = [points[forkFrom]];
    let [fx, fy] = points[forkFrom];
    const lean = Math.random() < 0.5 ? -1 : 1;
    for (let i = 0; i < 4; i++) {
      fx += lean * rand(8, 22);
      fy += rand(14, 26);
      fork.push([fx, fy]);
    }
    return [points, fork];
  };

  // Bright, a short dip, a second flash, then a fade: how real lightning flickers.
  const brightness = (age) => {
    if (age < 0.07) return 1;
    if (age < 0.14) return 0.25;
    if (age < 0.22) return 0.8;
    return Math.max(0, 0.8 * (1 - (age - 0.22) / 0.4));
  };

  return (ctx, dt, t, wind, dark) => {
    if (nextStrike === null) nextStrike = t + rand(2, 5);
    if (!strike && t >= nextStrike) {
      strike = { start: t, paths: makeBolt() };
      nextStrike = t + rand(5, 12);
    }
    if (!strike) return;
    const age = t - strike.start;
    const b = brightness(age);
    if (age > 0.7) {
      strike = null;
      return;
    }
    if (flash) {
      ctx.fillStyle = `rgba(255, 255, 255, ${(dark ? 0.16 : 0.3) * b})`;
      ctx.fillRect(0, 0, W, H);
    }

    const stroke = (width, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      for (const path of strike.paths) {
        ctx.beginPath();
        path.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.stroke();
      }
    };
    stroke(5, dark ? `rgba(180, 200, 255, ${0.35 * b})` : `rgba(110, 120, 200, ${0.3 * b})`);
    stroke(1.6, dark ? `rgba(255, 255, 255, ${b})` : `rgba(80, 90, 170, ${0.9 * b})`);
  };
}

// Weather that falls is shown in a still frame as the heavy cloud it comes from.
const FALLING = ["drizzle", "rain", "storm", "snow", "noreaster"];

function buildLayers(scene, W, H, { calm, still }) {
  const area = (W * H) / 10000; // in units of 100 x 100 px
  const count = (perArea, max) => Math.min(max, Math.round(area * perArea));
  const i = scene.intensity;
  const cloudCount = Math.max(2, Math.round((2 + 4 * i) * Math.min(W / 1400, 1)));

  if (still && FALLING.includes(scene.kind)) {
    return [clouds(W, H, { count: Math.max(cloudCount, 4), heavy: true })];
  }

  switch (scene.kind) {
    case "clear":
      return [
        skyLight(W, H, { night: !scene.isDay }),
        ...(scene.isDay ? [] : [stars(W, H, { count: count(0.6, 90) })]),
      ];
    case "clouds":
      return [
        ...(scene.isDay || i > 0.6 ? [] : [stars(W, H, { count: count(0.3, 45) })]),
        clouds(W, H, { count: cloudCount, heavy: i > 0.6 }),
      ];
    case "fog":
      return [fog(W, H, { intensity: i })];
    case "drizzle":
      return [rain(W, H, { count: count(2.2 * (0.5 + 0.5 * i), 260), drizzle: true })];
    case "rain":
      return [rain(W, H, { count: count(1.2 * (0.4 + i), 220) })];
    case "storm":
      return [
        tint(W, H, { light: "rgba(30, 40, 60, 0.07)", dark: "rgba(5, 10, 25, 0.18)" }),
        rain(W, H, { count: count(2.2, 300) }),
        lightning(W, H, { flash: !calm }),
      ];
    case "snow":
      return [snow(W, H, { count: count(1.1 * (0.4 + i), 180) })];
    case "noreaster":
      return [
        tint(W, H, { light: "rgba(150, 165, 190, 0.10)", dark: "rgba(200, 210, 230, 0.06)" }),
        snow(W, H, { count: count(2.4, 360), blizzard: true }),
      ];
    default:
      return [];
  }
}

export default function WeatherSky() {
  const weather = useWeather();
  const scene = weather?.scene ?? null;
  // There is a scene but it should not move: the visitor asked for reduced motion.
  const still = Boolean(scene) && !weather.animate;
  const calm = Boolean(weather?.reducedMotion);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!scene) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let W = 0;
    let H = 0;
    let layers = [];
    let frame = null;
    let last = null;
    let visible = true;

    // Draws one frame; with dt = 0 nothing moves.
    const draw = (now, dt) => {
      const t = now / 1000;
      const dark = Boolean(canvas.closest(".dark"));
      ctx.clearRect(0, 0, W, H);
      for (const layer of layers) layer(ctx, dt, t, gustyWind(scene, t), dark);
      canvas.style.opacity = "1"; // fades in after the first frame is drawn
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === W && rect.height === H) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = rect.width;
      H = rect.height;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      layers = buildLayers(scene, W, H, { calm, still });
      // Resizing empties the canvas, so draw straight away rather than show a blank frame.
      draw(performance.now(), 0);
    };

    const tick = (now) => {
      const dt = Math.min((now - (last ?? now)) / 1000, 1 / 30);
      last = now;
      draw(now, dt);
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (frame !== null || !visible) return;
      last = null;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    if (still) {
      // A still frame is only redrawn when the theme (a class on an ancestor) changes.
      const themeObserver = new MutationObserver(() => draw(performance.now(), 0));
      for (let el = canvas.parentElement; el; el = el.parentElement) {
        themeObserver.observe(el, { attributes: true, attributeFilter: ["class"] });
      }
      return () => {
        resizeObserver.disconnect();
        themeObserver.disconnect();
      };
    }

    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
      else stop();
    });
    visibilityObserver.observe(canvas);
    start();

    return () => {
      stop();
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, [scene, calm, still]);

  if (!scene) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{
        opacity: 0,
        transition: "opacity 0.8s",
        // fade out towards the bottom so the weather has no hard edge
        maskImage: "linear-gradient(to bottom, black 65%, transparent)",
        WebkitMaskImage: "linear-gradient(to bottom, black 65%, transparent)",
      }}
    />
  );
}
